import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Camera, Trash2, Receipt, Users, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { api, errMsg } from '../lib/api';
import { Avatar } from '../components/SocialBits';

const toJpeg = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const max = 512;
    const scale = Math.min(1, max / Math.max(img.width, img.height));
    const c = document.createElement('canvas');
    c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    c.toBlob((b) => (b ? resolve(b) : reject(new Error('convert failed'))), 'image/jpeg', 0.85);
  };
  img.onerror = reject;
  img.src = URL.createObjectURL(file);
});

function AvatarEditor({ user, onChanged }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(0);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const blob = await toJpeg(file);
      const fd = new FormData(); fd.append('file', blob, 'avatar.jpg');
      await api.post('/user/avatar', fd);
      toast.success('Profile picture updated'); setBust(Date.now()); onChanged();
    } catch (err) { toast.error(errMsg(err, 'Upload failed — try a different photo.')); } finally { setBusy(false); e.target.value = ''; }
  };
  const remove = async () => { try { await api.delete('/user/avatar'); onChanged(); } catch (err) { toast.error(errMsg(err)); } };
  return (
    <div className="flex items-center gap-5" data-testid="avatar-editor">
      <div className="relative">
        <Avatar user={{ name: user?.name, avatar_url: user?.avatar_url ? `${user.avatar_url}${user.avatar_url.includes('?') ? '&' : '?'}v=${bust}` : null }} size={20} />
        {busy && <div className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-white" /></div>}
      </div>
      <div className="flex gap-2">
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="avatar-file-input" />
        <button data-testid="avatar-upload-btn" onClick={() => ref.current?.click()} className="px-4 py-2 rounded-full bg-[#9333EA] text-white text-xs font-semibold hover:bg-[#7E22CE] transition-colors flex items-center gap-1.5"><Camera className="h-3.5 w-3.5" />{user?.avatar_url ? 'Change photo' : 'Add photo'}</button>
        {user?.avatar_url && <button data-testid="avatar-remove-btn" onClick={remove} className="px-3 py-2 rounded-full border border-white/10 text-[#A19DB0] text-xs hover:text-red-300 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>}
      </div>
    </div>
  );
}

function ContactForm({ user, onSaved }) {
  const [form, setForm] = useState({ name: user?.name || '', phone_number: user?.phone_number || '', backup_email: user?.backup_email || '' });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.patch('/user/profile', form); toast.success('Profile saved'); onSaved(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const field = (key, label, hint, props = {}) => (
    <label className="block space-y-1.5">
      <span className="text-xs font-semibold text-white">{label}</span>
      <Input data-testid={`profile-${key.replace('_', '-')}-input`} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="input-dark h-11" {...props} />
      {hint && <span className="text-[11px] text-[#A19DB0]">{hint}</span>}
    </label>
  );
  return (
    <div className="space-y-4" data-testid="contact-form">
      {field('name', 'Name')}
      {field('phone_number', 'Phone number (optional)', 'Used only for important account and refund updates.', { type: 'tel', placeholder: '(555) 123-4567' })}
      {field('backup_email', 'Backup email (optional)', "We'll use this if emails to your main address bounce.", { type: 'email', placeholder: 'you@example.com' })}
      <button data-testid="profile-save-btn" disabled={busy} onClick={save} className="w-full h-11 rounded-xl btn-primary font-semibold disabled:opacity-50">{busy ? 'Saving…' : 'Save changes'}</button>
    </div>
  );
}

export default function ProfilePage() {
  const { user, checkAuth } = useAuth();
  return (
    <div className="min-h-screen bg-vault" data-testid="profile-page">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link to="/dashboard" data-testid="profile-back-link" className="text-[#A19DB0] hover:text-white transition-colors"><ArrowLeft className="h-5 w-5" /></Link>
          <span className="font-heading text-lg font-bold text-white tracking-tight">Profile</span>
        </div>
      </header>
      <main className="max-w-xl mx-auto px-4 py-8 space-y-8">
        <AvatarEditor user={user} onChanged={checkAuth} />
        <p className="text-xs text-[#A19DB0] -mt-4" data-testid="profile-email">Signed in as {user?.email}</p>
        <ContactForm key={user?.id} user={user} onSaved={checkAuth} />
        <div className="grid grid-cols-2 gap-3">
          <Link to="/my-refunds" data-testid="profile-refunds-link" className="p-4 rounded-xl border border-white/10 bg-[#0A0A0F] hover:border-[#9333EA]/40 transition-colors flex items-center gap-2 text-sm text-white"><Receipt className="h-4 w-4 text-[#9333EA]" />My Refunds</Link>
          <Link to="/friends" data-testid="profile-friends-link" className="p-4 rounded-xl border border-white/10 bg-[#0A0A0F] hover:border-[#9333EA]/40 transition-colors flex items-center gap-2 text-sm text-white"><Users className="h-4 w-4 text-[#9333EA]" />Friends & Groups</Link>
        </div>
      </main>
    </div>
  );
}
